import * as React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CommandPalette } from '@/features/search/command-palette';
import { SHORTCUTS, useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * The authenticated frame: fixed navigation on the left, a top bar, and the
 * routed page in the middle. Only the main region scrolls, so the nav and
 * search stay put while a long list moves.
 */
export function AppShell() {
  const [isMobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isHelpOpen, setHelpOpen] = React.useState(false);
  const [isSearchOpen, setSearchOpen] = React.useState(false);

  useKeyboardShortcuts({
    onFocusSearch: () => setSearchOpen(true),
    onShowHelp: () => setHelpOpen(true),
  });

  return (
    <div className="flex h-screen overflow-hidden bg-surface-sunken">
      {/* First tab stop on every page: jump straight past the navigation. */}
      <a
        href="#main-content"
        className="sr-only-focusable absolute left-4 top-4 z-[100] rounded-md bg-brand px-4 py-2 text-sm font-medium text-white"
      >
        Skip to main content
      </a>

      <aside className="hidden w-60 shrink-0 border-r border-border-subtle bg-surface-raised lg:block">
        <div className="flex h-14 items-center gap-2 border-b border-border-subtle px-5">
          <svg viewBox="0 0 32 32" className="size-6" aria-hidden="true">
            <rect width="32" height="32" rx="8" className="fill-brand" />
            <path d="M9 22V10h2.6l7.2 8.2V10H22v12h-2.6L12.2 13.8V22H9z" fill="#fff" />
          </svg>
          <span className="font-semibold tracking-tight text-ink">WHNUNIVERSE</span>
        </div>
        <div className="h-[calc(100%-3.5rem)]">
          <Sidebar />
        </div>
      </aside>

      {/* On small screens the same nav becomes a modal drawer. */}
      <Dialog open={isMobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent className="left-0 top-0 h-full max-w-64 translate-x-0 translate-y-0 rounded-none border-y-0 border-l-0">
          <DialogHeader className="pb-2">
            <DialogTitle>Navigation</DialogTitle>
            <DialogDescription className="sr-only-focusable">
              Move between the sections of WHNUNIVERSE
            </DialogDescription>
          </DialogHeader>
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenSidebar={() => setMobileNavOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <main id="main-content" tabIndex={-1} className="relative flex-1 overflow-y-auto focus:outline-none">
          <Outlet />
        </main>
      </div>

      <Dialog open={isHelpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Keyboard shortcuts</DialogTitle>
            <DialogDescription>Shortcuts are ignored while you are typing.</DialogDescription>
          </DialogHeader>
          <dl className="space-y-2 px-6 pb-6">
            {SHORTCUTS.map((shortcut) => (
              <div key={shortcut.keys} className="flex items-center justify-between gap-4 text-sm">
                <dt className="text-ink-muted">{shortcut.description}</dt>
                <dd>
                  <kbd className="rounded border border-border-subtle bg-surface-sunken px-2 py-0.5 font-mono text-xs text-ink">
                    {shortcut.keys}
                  </kbd>
                </dd>
              </div>
            ))}
          </dl>
        </DialogContent>
      </Dialog>

      <CommandPalette open={isSearchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
